import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface BankReconciliation {
  id: string
  statementDate: string
  statementBalance: string
  reconciledBalance: string
  difference: string
  status: string
  bankAccount: {
    id: string
    account: {
      code: string
      name: string
    }
  }
  transactions?: BankTransaction[]
}

interface BankTransaction {
  id: string
  transactionDate: string
  description: string
  amount: string
  type: string
  reference?: string
  isReconciled: boolean
}

interface BankAccount {
  id: string
  account: {
    code: string
    name: string
  }
}

const BankReconciliation: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [reconciliations, setReconciliations] = useState<BankReconciliation[]>([])
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showBankFeedModal, setShowBankFeedModal] = useState(false)
  const [showBankFeedsList, setShowBankFeedsList] = useState(false)
  const [selectedReconciliation, setSelectedReconciliation] = useState<BankReconciliation | null>(null)
  const [bankFeeds, setBankFeeds] = useState<any[]>([])
  const [providers, setProviders] = useState<any[]>([])
  const [isSyncing, setIsSyncing] = useState<string | null>(null)
  const [bankFeedFormData, setBankFeedFormData] = useState({
    bankAccountId: '',
    provider: '',
    providerAccountId: '',
    accessToken: '',
    refreshToken: '',
    syncFrequency: 'DAILY',
    autoReconcile: false,
  })
  const [isLoadingReconciliation, setIsLoadingReconciliation] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isAutoMatching, setIsAutoMatching] = useState(false)
  const [importResult, setImportResult] = useState<any>(null)
  const [importFormData, setImportFormData] = useState({
    bankAccountId: '',
    reconciliationId: '',
    skipHeader: true,
    format: 'single', // 'single' or 'separate'
  })
  const [formData, setFormData] = useState({
    bankAccountId: '',
    statementDate: new Date().toISOString().split('T')[0],
    statementBalance: '',
    notes: '',
  })

  useEffect(() => {
    fetchReconciliations()
    fetchBankAccounts()
    fetchBankFeeds()
    fetchProviders()
  }, [])

  const fetchBankFeeds = async () => {
    try {
      const response = await axios.get('/api/erp/bank-feeds')
      setBankFeeds(response.data.data || [])
    } catch (error) {
      console.error('Error fetching bank feeds:', error)
    }
  }

  const fetchProviders = async () => {
    try {
      const response = await axios.get('/api/erp/bank-feeds/providers')
      setProviders(response.data.data || [])
    } catch (error) {
      console.error('Error fetching providers:', error)
    }
  }

  const fetchReconciliations = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/bank-reconciliations')
      setReconciliations(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching bank reconciliations:', error)
      toast.error('Failed to load bank reconciliations')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/bank-accounts')
      setBankAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching bank accounts:', error)
    }
  }

  const handleDownloadTemplate = async (format: 'single' | 'separate') => {
    try {
      const response = await axios.get(`/api/erp/bank-reconciliations/import/template?format=${format}`, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `bank-statement-template-${format}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Template downloaded')
    } catch (error: any) {
      console.error('Error downloading template:', error)
      toast.error('Failed to download template')
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!['csv', 'xlsx', 'xls'].includes(ext || '')) {
        toast.error('Please select a CSV or Excel file')
        return
      }
      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import')
      return
    }

    if (!importFormData.bankAccountId) {
      toast.error('Please select a bank account')
      return
    }

    setIsImporting(true)
    setImportResult(null)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('bankAccountId', importFormData.bankAccountId)
      if (importFormData.reconciliationId) {
        formData.append('reconciliationId', importFormData.reconciliationId)
      }
      formData.append('skipHeader', importFormData.skipHeader.toString())
      formData.append('format', importFormData.format)

      const response = await axios.post('/api/erp/bank-reconciliations/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      setImportResult(response.data.data)
      
      if (response.data.success) {
        toast.success(`Import completed: ${response.data.data.imported} imported, ${response.data.data.matched} matched, ${response.data.data.failed} failed`)
        fetchReconciliations()
        if (importFormData.reconciliationId) {
          // Refresh the selected reconciliation if importing into one
          handleViewReconciliation(importFormData.reconciliationId)
        }
      } else {
        toast.error(`Import completed with errors: ${response.data.data.imported} imported, ${response.data.data.failed} failed`)
      }
    } catch (error: any) {
      console.error('Error importing bank statement:', error)
      toast.error(error.response?.data?.error || 'Failed to import bank statement')
      if (error.response?.data?.data) {
        setImportResult(error.response.data.data)
      }
    } finally {
      setIsImporting(false)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.bankAccountId || !formData.statementDate || !formData.statementBalance) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.post('/api/erp/bank-reconciliations', {
        bankAccountId: formData.bankAccountId,
        statementDate: formData.statementDate,
        statementBalance: parseFloat(formData.statementBalance),
        notes: formData.notes || undefined,
      })
      toast.success('Bank reconciliation created successfully')
      setShowCreateModal(false)
      setFormData({
        bankAccountId: '',
        statementDate: new Date().toISOString().split('T')[0],
        statementBalance: '',
        notes: '',
      })
      fetchReconciliations()
    } catch (error: any) {
      console.error('Error creating bank reconciliation:', error)
      toast.error(error.response?.data?.error || 'Failed to create bank reconciliation')
    }
  }

  const handleViewReconciliation = async (id: string) => {
    setIsLoadingReconciliation(true)
    try {
      const response = await axios.get(`/api/erp/bank-reconciliations/${id}`)
      setSelectedReconciliation(response.data.data)
      setShowViewModal(true)
    } catch (error: any) {
      console.error('Error fetching bank reconciliation:', error)
      toast.error('Failed to load bank reconciliation details')
    } finally {
      setIsLoadingReconciliation(false)
    }
  }

  const handleAutoMatch = async (id: string) => {
    if (!confirm('This will automatically match bank transactions to payments and GL entries. Continue?')) {
      return
    }
    setIsAutoMatching(true)
    try {
      const response = await axios.post(`/api/erp/bank-reconciliations/${id}/auto-match`, {
        dateToleranceDays: 7,
        amountTolerance: 0,
        matchByReference: true,
        matchByDescription: false,
      })
      const result = response.data.data
      toast.success(`Automatic matching completed: ${result.matched} matched, ${result.unmatched} unmatched`)
      
      // Refresh reconciliation
      fetchReconciliations()
      if (selectedReconciliation && selectedReconciliation.id === id) {
        const reconciliationResponse = await axios.get(`/api/erp/bank-reconciliations/${id}`)
        setSelectedReconciliation(reconciliationResponse.data.data)
      }
    } catch (error: any) {
      console.error('Error auto-matching:', error)
      toast.error(error.response?.data?.error || 'Failed to perform automatic matching')
    } finally {
      setIsAutoMatching(false)
    }
  }

  const handleReconcile = async (id: string) => {
    if (!confirm('Are you sure you want to reconcile this bank statement? This will mark all matched transactions as reconciled.')) {
      return
    }
    try {
      await axios.post(`/api/erp/bank-reconciliations/${id}/reconcile`)
      toast.success('Bank reconciliation completed successfully')
      fetchReconciliations()
      if (selectedReconciliation && selectedReconciliation.id === id) {
        const response = await axios.get(`/api/erp/bank-reconciliations/${id}`)
        setSelectedReconciliation(response.data.data)
      }
    } catch (error: any) {
      console.error('Error reconciling:', error)
      toast.error(error.response?.data?.error || 'Failed to reconcile')
    }
  }

  if (permissionsLoading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading permissions...</div>
    )
  }

  if (!hasPermission('erp_bank_reconciliation', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view bank reconciliations.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Bank Reconciliation</h1>
            <PageHelp pageIdentifier="bank-reconciliation" title="Bank Reconciliation" />
          </div>
          <p className="text-gray-600 mt-1">Reconcile bank statements with your accounting records</p>
        </div>
        {hasPermission('erp_bank_reconciliation', 'write') && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowBankFeedsList(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Bank Feeds</span>
            </button>
            <button
              onClick={() => {
                setImportFormData({ ...importFormData, bankAccountId: '', reconciliationId: '' })
                setShowImportModal(true)
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Import Statement</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Reconciliation</span>
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : reconciliations.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No bank reconciliations found. Create your first reconciliation to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statement Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Statement Balance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reconciled Balance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reconciliations.map((reconciliation) => (
                <tr key={reconciliation.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewReconciliation(reconciliation.id)}>
                  <td className="px-4 py-2">
                    {reconciliation.bankAccount.account.code} - {reconciliation.bankAccount.account.name}
                  </td>
                  <td className="px-4 py-2">{new Date(reconciliation.statementDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(reconciliation.statementBalance).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(reconciliation.reconciledBalance).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${
                    parseFloat(reconciliation.difference) === 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {parseFloat(reconciliation.difference).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      reconciliation.status === 'RECONCILED' ? 'bg-green-100 text-green-800' :
                      reconciliation.status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {reconciliation.status}
                    </span>
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewReconciliation(reconciliation.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingReconciliation}
                      >
                        View
                      </button>
                      {reconciliation.status === 'OPEN' && hasPermission('erp_bank_reconciliation', 'reconcile') && (
                        <button
                          onClick={() => handleReconcile(reconciliation.id)}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Reconcile
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Reconciliation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">New Bank Reconciliation</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account *</label>
                  <select
                    required
                    value={formData.bankAccountId}
                    onChange={(e) => setFormData({ ...formData, bankAccountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select bank account...</option>
                    {bankAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account.code} - {acc.account.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statement Date *</label>
                  <input
                    type="date"
                    required
                    value={formData.statementDate}
                    onChange={(e) => setFormData({ ...formData, statementDate: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statement Balance *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.statementBalance}
                    onChange={(e) => setFormData({ ...formData, statementBalance: e.target.value })}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Additional notes..."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Reconciliation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Reconciliation Modal */}
      {showViewModal && selectedReconciliation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Bank Reconciliation</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedReconciliation(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Bank Account</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedReconciliation.bankAccount.account.code} - {selectedReconciliation.bankAccount.account.name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statement Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedReconciliation.statementDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedReconciliation.status === 'RECONCILED' ? 'bg-green-100 text-green-800' :
                      selectedReconciliation.status === 'OPEN' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedReconciliation.status}
                    </span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statement Balance</label>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {parseFloat(selectedReconciliation.statementBalance).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reconciled Balance</label>
                  <p className="mt-1 text-lg font-bold text-gray-900">
                    {parseFloat(selectedReconciliation.reconciledBalance).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Difference</label>
                  <p className={`mt-1 text-lg font-bold ${
                    parseFloat(selectedReconciliation.difference) === 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {parseFloat(selectedReconciliation.difference).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </p>
                </div>
              </div>

              {/* Transactions */}
              {selectedReconciliation.transactions && selectedReconciliation.transactions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Transactions</label>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedReconciliation.transactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td className="px-4 py-2">{new Date(transaction.transactionDate).toLocaleDateString()}</td>
                            <td className="px-4 py-2">{transaction.description}</td>
                            <td className="px-4 py-2">{transaction.reference || '-'}</td>
                            <td className="px-4 py-2 text-right">{parseFloat(transaction.amount).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                            <td className="px-4 py-2">{transaction.type}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                transaction.isReconciled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {transaction.isReconciled ? 'Reconciled' : 'Pending'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <div className="flex gap-2">
                {selectedReconciliation.status === 'OPEN' && hasPermission('erp_bank_reconciliation', 'write') && (
                  <button
                    type="button"
                    onClick={() => handleAutoMatch(selectedReconciliation.id)}
                    disabled={isAutoMatching}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    {isAutoMatching ? 'Matching...' : 'Auto Match'}
                  </button>
                )}
                {selectedReconciliation.status === 'OPEN' && hasPermission('erp_bank_reconciliation', 'reconcile') && (
                  <button
                    type="button"
                    onClick={() => handleReconcile(selectedReconciliation.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Complete Reconciliation
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedReconciliation(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Feeds List Modal */}
      {showBankFeedsList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Bank Feeds</h2>
              <div className="flex gap-2">
                {hasPermission('erp_bank_reconciliation', 'write') && (
                  <button
                    onClick={() => {
                      setBankFeedFormData({
                        bankAccountId: '',
                        provider: '',
                        providerAccountId: '',
                        accessToken: '',
                        refreshToken: '',
                        syncFrequency: 'DAILY',
                        autoReconcile: false,
                      })
                      setShowBankFeedModal(true)
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Add Bank Feed
                  </button>
                )}
                <button
                  onClick={() => setShowBankFeedsList(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {bankFeeds.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No bank feeds configured. Add one to get started.</p>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bank Account</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sync Frequency</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Sync</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bankFeeds.map((feed) => (
                    <tr key={feed.id}>
                      <td className="px-4 py-2">
                        {feed.bankAccount?.account?.code} - {feed.bankAccount?.account?.name}
                      </td>
                      <td className="px-4 py-2">{feed.provider}</td>
                      <td className="px-4 py-2">{feed.syncFrequency}</td>
                      <td className="px-4 py-2">
                        {feed.lastSyncAt ? new Date(feed.lastSyncAt).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          feed.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {feed.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {feed.lastSyncStatus && (
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                            feed.lastSyncStatus === 'SUCCESS' ? 'bg-blue-100 text-blue-800' :
                            feed.lastSyncStatus === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {feed.lastSyncStatus}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex space-x-2">
                          {feed.isActive && hasPermission('erp_bank_reconciliation', 'write') && (
                            <button
                              onClick={() => handleSyncBankFeed(feed.id)}
                              disabled={isSyncing === feed.id}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:text-gray-400"
                            >
                              {isSyncing === feed.id ? 'Syncing...' : 'Sync'}
                            </button>
                          )}
                          {hasPermission('erp_bank_reconciliation', 'write') && (
                            <button
                              onClick={() => handleDeleteBankFeed(feed.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Create Bank Feed Modal */}
      {showBankFeedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Add Bank Feed</h2>
            <form onSubmit={handleCreateBankFeed}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account *</label>
                  <select
                    required
                    value={bankFeedFormData.bankAccountId}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, bankAccountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select bank account...</option>
                    {bankAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account.code} - {acc.account.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
                  <select
                    required
                    value={bankFeedFormData.provider}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, provider: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select provider...</option>
                    {providers.map(provider => (
                      <option key={provider.value} value={provider.value}>
                        {provider.label} - {provider.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider Account ID</label>
                  <input
                    type="text"
                    value={bankFeedFormData.providerAccountId}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, providerAccountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="External account ID from provider"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Access Token *</label>
                  <input
                    type="password"
                    required
                    value={bankFeedFormData.accessToken}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, accessToken: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="API access token"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token</label>
                  <input
                    type="password"
                    value={bankFeedFormData.refreshToken}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, refreshToken: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    placeholder="API refresh token (optional)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sync Frequency</label>
                  <select
                    value={bankFeedFormData.syncFrequency}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, syncFrequency: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="MANUAL">Manual</option>
                    <option value="DAILY">Daily</option>
                    <option value="WEEKLY">Weekly</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bankFeedFormData.autoReconcile}
                    onChange={(e) => setBankFeedFormData({ ...bankFeedFormData, autoReconcile: e.target.checked })}
                    className="rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Auto-reconcile transactions</label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                  <p className="font-semibold mb-1">Note:</p>
                  <p>Bank feed integration requires API credentials from the provider. The structure is ready for Plaid, Yodlee, and Open Banking integrations.</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowBankFeedModal(false)
                    setBankFeedFormData({
                      bankAccountId: '',
                      provider: '',
                      providerAccountId: '',
                      accessToken: '',
                      refreshToken: '',
                      syncFrequency: 'DAILY',
                      autoReconcile: false,
                    })
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BankReconciliation
